/**
 * Analytics API Service
 * Analytics operations following FRONTEND_API_GUIDE (Admin only)
 */

import { apiService } from '@/shared/services';
import { API_ENDPOINTS } from '@/shared/constants';

// Types matching FRONTEND_API_GUIDE
export interface UserCountMetrics {
  current_period: {
    total_users: number;
    active_users: number;
    new_users: number;
  };
  previous_period: {
    total_users: number;
    active_users: number;
    new_users: number;
  };
  growth: {
    total_users_percent: number;
    active_users_percent: number;
    new_users_percent: number;
  };
  period: string;
  updated_at: string;
}

export interface ActiveSessionMetrics {
  active_sessions: number;
  peak_concurrent: number;
  average_session_duration_minutes: number;
  sessions_by_hour: Array<{
    hour: string;
    count: number;
  }>;
}

export interface UserGrowthData {
  periods: Array<{
    period_label: string;
    start_date: string;
    end_date: string;
    user_count: number;
    new_users: number;
    percentage_of_max: number;
  }>;
  max_count: number;
  total_new_users: number;
  average_growth_rate: number;
}

export class AnalyticsApiService {
  async getUserCount(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<UserCountMetrics> {
    const response = await apiService.get<UserCountMetrics>(
      `${API_ENDPOINTS.ANALYTICS.USERS_COUNT}?period=${period}`
    );
    // Backend may return { current_count, ... } - normalize to expected format
    const data = response.data;
    if (data && 'current_count' in data) {
      const currentCount = (data as unknown as { current_count?: number }).current_count || 0;
      return {
        ...data,
        current_period: {
          total_users: currentCount,
          active_users: data.current_period?.active_users || currentCount,
          new_users: data.current_period?.new_users || 0,
        },
      } as UserCountMetrics;
    }
    return data;
  }

  async getActiveSessions(): Promise<ActiveSessionMetrics> {
    const response = await apiService.get<ActiveSessionMetrics>(API_ENDPOINTS.ANALYTICS.SESSIONS_ACTIVE);
    // Backend returns { current_active, ... } but frontend expects { active_sessions, ... }
    const data = response.data;
    if (data && typeof data === 'object' && 'current_active' in data && !('active_sessions' in data)) {
      return {
        ...(data as Record<string, unknown>),
        active_sessions: (data as Record<string, unknown>).current_active as number || 0,
      } as ActiveSessionMetrics;
    }
    return data;
  }

  async getUserGrowth(): Promise<UserGrowthData> {
    const response = await apiService.get<UserGrowthData>(API_ENDPOINTS.ANALYTICS.USERS_GROWTH);
    return response.data;
  }

  async trackCalculatorUsage(fromCity: string, toCity: string, lifestyleTier: string): Promise<void> {
    try {
      await apiService.post('/analytics/calculator-usage', {
        from_city: fromCity,
        to_city: toCity,
        lifestyle_tier: lifestyleTier,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Silently fail for analytics
      console.warn('Failed to track calculator usage:', error);
    }
  }

  async trackPageView(data: {
    page_path: string;
    page_title?: string;
    referrer?: string;
    user_agent?: string;
    ip_address?: string;
    session_id?: string;
    device_type?: string;
    browser?: string;
    os?: string;
    time_on_page?: number;
    scroll_depth?: number;
    tracking_metadata?: Record<string, unknown>;
  }): Promise<void> {
    try {
      await apiService.post<void>(API_ENDPOINTS.ANALYTICS.TRACK_PAGEVIEW, data);
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.warn('Failed to track page view:', error);
    }
  }

  async getVisitorAnalytics(params?: {
    period?: 'day' | 'week' | 'month';
    start_date?: string;
    end_date?: string;
  }): Promise<{
    unique_visitors: number;
    total_page_views: number;
    unique_sessions: number;
    unique_users: number;
    unique_visitors_ip_ua: number;
    avg_page_views_per_visitor: number;
    period: string;
    start_date?: string;
    end_date: string;
    daily_views: Record<string, number>;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);

    const url = `${API_ENDPOINTS.ANALYTICS.VISITORS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiService.get<{
      unique_visitors: number;
      total_page_views: number;
      unique_sessions: number;
      unique_users: number;
      unique_visitors_ip_ua: number;
      avg_page_views_per_visitor: number;
      period: string;
      start_date?: string;
      end_date: string;
      daily_views: Record<string, number>;
    }>(url);
    return response.data;
  }

  async getTrafficSources(params?: {
    period?: 'day' | 'week' | 'month';
    start_date?: string;
    end_date?: string;
  }): Promise<{
    sources: Array<{
      source_type: string;
      page_views: number;
      unique_sessions: number;
      unique_users: number;
      avg_time_on_page_seconds: number;
      bounce_rate?: number;
      percentage: number;
    }>;
    total_page_views: number;
    period: string;
    start_date?: string;
    end_date: string;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);

    const url = `${API_ENDPOINTS.ANALYTICS.TRAFFIC_SOURCES}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiService.get<{
      sources: Array<{
        source_type: string;
        page_views: number;
        unique_sessions: number;
        unique_users: number;
        avg_time_on_page_seconds: number;
        bounce_rate?: number;
        percentage: number;
      }>;
      total_page_views: number;
      period: string;
      start_date?: string;
      end_date: string;
    }>(url);
    return response.data;
  }
}

// Export singleton instance
export const analyticsApiService = new AnalyticsApiService();
